import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Ban, CalendarClock, Check, Crown, Download, Inbox, Mail, MailCheck, Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const prettyDate = (key) =>
  new Date(`${key}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const prettyDateTime = (iso) =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const TABS = [
  { id: "bookings", label: "Bookings", icon: CalendarClock, testid: "owner-tab-bookings" },
  { id: "requests", label: "Requests", icon: Inbox, testid: "owner-tab-requests" },
  { id: "blackouts", label: "Blackout Days", icon: Ban, testid: "owner-tab-blackouts" },
];

export const OwnerPage = () => {
  const [key, setKey] = useState(localStorage.getItem("va_admin_key") || "");
  const [unlocked, setUnlocked] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [blackouts, setBlackouts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [notes, setNotes] = useState({});
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const loadAll = async (k) => {
    const h = { "X-Admin-Key": k };
    const [bo, bk, rq] = await Promise.all([
      axios.get(`${API}/blackouts`),
      axios.get(`${API}/admin/bookings`, { headers: h }),
      axios.get(`${API}/admin/consultations`, { headers: h }),
    ]);
    setBlackouts(bo.data);
    setBookings(bk.data);
    setRequests(rq.data);
    setNotes(Object.fromEntries(bk.data.map((b) => [b.id, b.note || ""])));
  };

  const saveNote = async (b) => {
    try {
      await axios.patch(
        `${API}/admin/bookings/${b.id}/note`,
        { note: notes[b.id] || "" },
        { headers: { "X-Admin-Key": key } }
      );
      toast.success("Note saved", { description: b.name });
    } catch {
      toast.error("Could not save the note");
    }
  };

  const unlock = async (k) => {
    try {
      await loadAll(k);
      localStorage.setItem("va_admin_key", k);
      setUnlocked(true);
    } catch (err) {
      setUnlocked(false);
      if (err?.response?.status === 401) toast.error("Invalid admin key");
      else toast.error("Could not load the dashboard");
    }
  };

  useEffect(() => {
    if (key) unlock(key);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addBlackout = async (e) => {
    e.preventDefault();
    if (!date) return;
    setBusy(true);
    try {
      await axios.post(`${API}/blackouts`, { date, reason }, { headers: { "X-Admin-Key": key } });
      toast.success("Day blocked", { description: prettyDate(date) });
      setDate("");
      setReason("");
      loadAll(key);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid admin key");
        setUnlocked(false);
      } else toast.error("Could not block that day");
    } finally {
      setBusy(false);
    }
  };

  const removeBlackout = async (d) => {
    try {
      await axios.delete(`${API}/blackouts/${d}`, { headers: { "X-Admin-Key": key } });
      toast.success("Day reopened", { description: prettyDate(d) });
      loadAll(key);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid admin key");
        setUnlocked(false);
      } else toast.error("Could not remove that block");
    }
  };

  const toggleHandled = async (r) => {
    const next = !r.handled;
    setRequests((rs) => rs.map((x) => (x.id === r.id ? { ...x, handled: next } : x)));
    try {
      await axios.patch(
        `${API}/admin/consultations/${r.id}/handled`,
        { handled: next },
        { headers: { "X-Admin-Key": key } }
      );
      toast.success(next ? "Marked as handled" : "Request reopened");
    } catch {
      setRequests((rs) => rs.map((x) => (x.id === r.id ? { ...x, handled: !next } : x)));
      toast.error("Could not update that request");
    }
  };

  const sendDigest = async () => {
    try {
      const res = await axios.post(`${API}/admin/digest/send`, {}, { headers: { "X-Admin-Key": key } });
      toast.success("Digest sent", {
        description: `${res.data.bookings} appointment(s), ${res.data.requests} new request(s) — check robertaraehare.gfi@gmail.com`,
      });
    } catch {
      toast.error("Could not send the digest");
    }
  };

  const exportContacts = async () => {
    try {
      const res = await axios.get(`${API}/admin/contacts/export`, {
        headers: { "X-Admin-Key": key },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vision-aries-contacts.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Contacts exported", { description: "Check your downloads folder." });
    } catch {
      toast.error("Could not export contacts");
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter((b) => b.status !== "cancelled" && b.date >= today);
  const pastOrCancelled = bookings.filter((b) => !(b.status !== "cancelled" && b.date >= today));

  return (
    <div className="min-h-screen bg-obsidian px-6 py-16">
      <div className="mx-auto w-full max-w-3xl">
        <Link to="/" className="mb-8 flex items-center gap-3" data-testid="owner-brand-link">
          <span className="flex h-9 w-9 items-center justify-center border border-gold/40 bg-royal/60 text-gold">
            <Crown size={16} strokeWidth={1.5} />
          </span>
          <span className="font-display text-sm font-semibold tracking-[0.22em] text-white">
            VISION ARIES
          </span>
        </Link>

        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Owner Dashboard</p>
        <h1 className="mt-3 font-display text-2xl font-semibold text-white sm:text-3xl">
          Your enquiries & schedule
        </h1>

        {!unlocked ? (
          <div className="gold-border-glow mt-8 bg-wine/70 p-8" data-testid="owner-lock-panel">
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
              Admin Key
            </label>
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Enter your admin key"
              data-testid="owner-key-input"
              className="w-full border border-white/15 bg-obsidian/60 px-4 py-3.5 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-gold/60"
            />
            <button
              onClick={() => unlock(key)}
              disabled={!key}
              data-testid="owner-unlock-button"
              className="mt-4 bg-gold px-8 py-3.5 font-mono text-xs uppercase tracking-[0.25em] text-obsidian transition-all duration-300 hover:bg-gold-light disabled:opacity-40"
            >
              Unlock Dashboard
            </button>
          </div>
        ) : (
          <div className="mt-8" data-testid="owner-dashboard">
            <div className="mb-4 flex flex-wrap justify-end gap-2">
              <button
                onClick={exportContacts}
                data-testid="owner-export-button"
                className="flex items-center gap-2 border border-white/20 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 transition-all duration-300 hover:border-gold/60 hover:text-gold"
              >
                <Download size={13} /> Export contacts
              </button>
              <button
                onClick={sendDigest}
                data-testid="owner-send-digest-button"
                className="flex items-center gap-2 border border-gold/40 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-obsidian"
              >
                <MailCheck size={13} /> Email me today’s digest
              </button>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="owner-tabs">
              {TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    data-testid={t.testid}
                    className={`flex items-center gap-2 border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-all duration-300 ${
                      tab === t.id
                        ? "border-gold bg-gold/15 text-gold"
                        : "border-white/15 text-white/50 hover:border-gold/40 hover:text-gold"
                    }`}
                  >
                    <Icon size={13} /> {t.label}
                  </button>
                );
              })}
            </div>

            {tab === "bookings" && (
              <div className="mt-6 space-y-3" data-testid="owner-bookings-list">
                {bookings.length === 0 && (
                  <p className="text-sm text-white/40" data-testid="owner-no-bookings">No bookings yet.</p>
                )}
                {upcoming.map((b) => (
                  <div key={b.token} className="gold-border-glow bg-wine/70 p-5" data-testid={`owner-booking-${b.token.slice(0, 8)}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-display text-lg text-white">{b.name}</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                          {prettyDate(b.date)} · {b.time} ET · {b.service}
                        </p>
                      </div>
                      <span className="border border-gold/40 bg-gold/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-gold">
                        {b.status}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-4 text-xs text-white/50">
                      <a href={`mailto:${b.email}`} className="flex items-center gap-1.5 hover:text-gold">
                        <Mail size={12} /> {b.email}
                      </a>
                      {b.phone && (
                        <a href={`tel:${b.phone}`} className="flex items-center gap-1.5 hover:text-gold">
                          <Phone size={12} /> {b.phone}
                        </a>
                      )}
                    </div>
                    <div className="mt-4 flex gap-2">
                      <input
                        value={notes[b.id] ?? ""}
                        onChange={(e) => setNotes((n) => ({ ...n, [b.id]: e.target.value }))}
                        placeholder="Private note — what does this client need?"
                        data-testid={`owner-note-input-${b.token.slice(0, 8)}`}
                        className="flex-1 border border-white/15 bg-obsidian/60 px-3 py-2 text-xs text-white placeholder-white/25 outline-none transition-colors focus:border-gold/60"
                      />
                      <button
                        onClick={() => saveNote(b)}
                        data-testid={`owner-note-save-${b.token.slice(0, 8)}`}
                        className="border border-gold/40 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-obsidian"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ))}
                {pastOrCancelled.length > 0 && (
                  <p className="pt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-white/30">
                    Past & cancelled
                  </p>
                )}
                {pastOrCancelled.map((b) => (
                  <div key={b.token} className="border border-white/10 bg-wine/30 p-5 opacity-60" data-testid={`owner-booking-past-${b.token.slice(0, 8)}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="text-sm text-white/70">
                        {b.name} — {prettyDate(b.date)} · {b.time} ET
                      </p>
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">
                        {b.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "requests" && (
              <div className="mt-6 space-y-3" data-testid="owner-requests-list">
                {requests.length === 0 && (
                  <p className="text-sm text-white/40" data-testid="owner-no-requests">No requests yet.</p>
                )}
                {requests.map((r) => (
                  <div
                    key={r.id}
                    className={`gold-border-glow bg-wine/70 p-5 transition-opacity duration-300 ${r.handled ? "opacity-50" : ""}`}
                    data-testid={`owner-request-${r.id.slice(0, 8)}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="font-display text-lg text-white">{r.name}</p>
                      <span className="flex items-center gap-3">
                        {r.handled && (
                          <span
                            className="flex items-center gap-1 border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-300"
                            data-testid={`owner-handled-badge-${r.id.slice(0, 8)}`}
                          >
                            <Check size={11} /> Handled
                          </span>
                        )}
                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">
                          {prettyDateTime(r.created_at)}
                        </span>
                      </span>
                    </div>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{r.service}</p>
                    {r.preferred_time && (
                      <p className="mt-2 text-xs text-white/50">Prefers: {r.preferred_time}</p>
                    )}
                    {r.message && <p className="mt-2 text-sm leading-relaxed text-white/55">{r.message}</p>}
                    <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-white/50">
                      <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 hover:text-gold">
                        <Mail size={12} /> {r.email}
                      </a>
                      {r.phone && (
                        <a href={`tel:${r.phone}`} className="flex items-center gap-1.5 hover:text-gold">
                          <Phone size={12} /> {r.phone}
                        </a>
                      )}
                      <button
                        onClick={() => toggleHandled(r)}
                        data-testid={`owner-handled-toggle-${r.id.slice(0, 8)}`}
                        className={`ml-auto flex items-center gap-1.5 border px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] transition-all duration-300 ${
                          r.handled
                            ? "border-white/15 text-white/40 hover:border-gold/40 hover:text-gold"
                            : "border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10"
                        }`}
                      >
                        <Check size={11} /> {r.handled ? "Reopen" : "Mark handled"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "blackouts" && (
              <div className="mt-6">
                <form onSubmit={addBlackout} className="flex flex-col gap-3 sm:flex-row">
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                    data-testid="owner-date-input"
                    className="border border-white/15 bg-obsidian/60 px-4 py-3 text-sm text-white outline-none [color-scheme:dark] focus:border-gold/60"
                  />
                  <input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Reason (optional)"
                    data-testid="owner-reason-input"
                    className="flex-1 border border-white/15 bg-obsidian/60 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-gold/60"
                  />
                  <button
                    type="submit"
                    disabled={busy || !date}
                    data-testid="owner-add-blackout-button"
                    className="flex items-center justify-center gap-2 bg-gold px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-obsidian transition-all duration-300 hover:bg-gold-light disabled:opacity-40"
                  >
                    <Ban size={13} /> Block
                  </button>
                </form>
                <div className="mt-6 space-y-2" data-testid="owner-blackout-list">
                  {blackouts.length === 0 ? (
                    <p className="text-sm text-white/40" data-testid="owner-no-blackouts">
                      No blocked days — every open slot is bookable.
                    </p>
                  ) : (
                    blackouts.map((b) => (
                      <div
                        key={b.date}
                        className="flex items-center justify-between border border-white/10 bg-obsidian/40 px-4 py-3"
                        data-testid={`owner-blackout-${b.date}`}
                      >
                        <span>
                          <span className="block font-mono text-xs tracking-widest text-gold">
                            {prettyDate(b.date)}
                          </span>
                          {b.reason && <span className="block text-xs text-white/40">{b.reason}</span>}
                        </span>
                        <button
                          onClick={() => removeBlackout(b.date)}
                          data-testid={`owner-delete-${b.date}`}
                          className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 transition-colors hover:text-red-300"
                        >
                          <Trash2 size={13} /> Reopen
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
